// TechPersonaCarousel.js — Tech Software Executive Briefing Edition
import React, { useState } from "react";

/* =========================
   1. Design Tokens & CSS
   ========================= */
const CSS = `
  :root {
    --brand: #0056D2;       
    --brand-10: #E6F0FF;
    --navy: #0B1A3C;        
    --navy-dark: #081226;
    --white: #FFFFFF;
    --ink: #0B1A3C;
    --muted: #64748B;
    --line: #E2E8F0;
    
    --orange: #F97316;
    --green: #10B981;
    --green-bg: #ECFDF5;
    
    --radius-lg: 20px;
    --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  }

  .carousel-wrap {
    font-family: "Source Sans Pro", sans-serif;
    padding: 40px 20px;
    background: #F8FAFC;
  }

  .carousel-container {
    max-width: 1000px;
    margin: 0 auto;
  }

  /* --- The Dark Card (Lens Style) --- */
  .lens-card {
    background: var(--navy-dark); color: white;
    border-radius: var(--radius-lg);
    border: 1px solid rgba(255,255,255,0.1);
    box-shadow: var(--shadow-xl);
    overflow: hidden;
    position: relative;
    transition: all 0.3s ease;
  }

  .lens-header {
    padding: 32px 40px;
    border-bottom: 1px solid rgba(255,255,255,0.1);
    background: linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%);
  }

  .lens-tag {
    display: inline-block; background: var(--brand); color: white;
    font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px;
    padding: 6px 12px; border-radius: 99px; margin-bottom: 16px;
  }

  .lens-title { font-size: 32px; font-weight: 700; margin: 0 0 8px 0; letter-spacing: -0.5px; }
  .lens-sub { font-size: 16px; color: rgba(255,255,255,0.6); margin: 0; line-height: 1.5; max-width: 600px; }

  .lens-grid {
    display: grid; grid-template-columns: 1.1fr 0.9fr;
    min-height: 480px;
  }
  @media (max-width: 800px) { .lens-grid { grid-template-columns: 1fr; } }

  .lens-left { padding: 40px; border-right: 1px solid rgba(255,255,255,0.1); }
  .lens-right { padding: 40px; background: rgba(255,255,255,0.02); }

  .lens-h3 {
    font-size: 12px; font-weight: 800; text-transform: uppercase; letter-spacing: 1.2px;
    margin-bottom: 24px; display: flex; align-items: center; gap: 8px;
  }
  .orange-text { color: var(--orange); }
  .green-text { color: var(--green); }
  
  .lens-list { list-style: none; padding: 0; margin: 0; }
  .lens-list li {
    position: relative; padding-left: 24px; margin-bottom: 16px;
    font-size: 15px; line-height: 1.6; color: rgba(255,255,255,0.9);
  }
  .lens-list li::before {
    content: "•"; position: absolute; left: 0; top: 0px; font-size: 18px;
  }
  .lens-left li::before { color: var(--orange); }
  
  /* White Card inside the Dark Card */
  .success-card {
    background: var(--white); color: var(--navy);
    border-radius: 12px; padding: 24px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.2);
  }
  .success-list { list-style: none; padding: 0; margin: 0; }
  .success-list li {
    display: flex; gap: 12px; margin-bottom: 16px; font-size: 15px; line-height: 1.5;
  }
  .check-icon {
    min-width: 20px; height: 20px; border-radius: 50%; border: 1.5px solid var(--green);
    display: grid; place-items: center; color: var(--green); font-size: 12px; font-weight: 900;
  }

  /* --- Navigation Controls --- */
  .nav-strip {
    display: flex; gap: 12px; justify-content: center; margin-bottom: 24px; flex-wrap: wrap;
  }
  .nav-btn {
    background: white; border: 1px solid var(--line);
    padding: 10px 16px; border-radius: 99px;
    font-size: 13px; font-weight: 600; color: var(--muted);
    cursor: pointer; transition: all 0.2s;
    display: flex; align-items: center; gap: 8px;
  }
  .nav-btn:hover { border-color: var(--brand); color: var(--brand); }
  .nav-btn.active {
    background: var(--brand); border-color: var(--brand); color: white;
    box-shadow: 0 4px 12px rgba(0, 86, 210, 0.3);
  }
  .nav-circle {
    width: 8px; height: 8px; border-radius: 50%; background: currentColor; opacity: 0.5;
  }
  .nav-btn.active .nav-circle { opacity: 1; background: white; }

  /* --- Source Citation Footer --- */
  .source-footer {
    text-align: center; margin-top: 24px; font-size: 12px; color: var(--muted);
  }
  .source-footer a { color: var(--brand); text-decoration: none; }
`;

/* =========================
   2. Data: Extracted from Tech Executive Briefing
   ========================= */
const TECH_DATA = [
  {
    id: "engineering",
    role: "Engineering Leader",
    title: "CTO / VP Engineering",
    desc: "Innovating at speed while managing technical debt and security risks.",
    nightmare: [
      "Only 24% of developers consider themselves GenAI experts.",
      "Modernization bottlenecks: Legacy systems slowing down agile innovation.",
      "Teams moving from 'AI adoption' to 'AI orchestration' without the skills to validate outcomes."
    ],
    success: [
      "Refactoring legacy systems into scalable architectures.",
      "Teams successfully supervising and validating AI-generated code.",
      "Reduced technical debt through cloud/DevOps mastery."
    ],
    coursera_unlock: "Cloud & DevOps Academies + Guided Projects"
  },
  {
    id: "hr-ld",
    role: "HR & L&D Leader",
    title: "CHRO / Head of Talent",
    desc: "Building a culture of continuous innovation and human-centric skills.",
    nightmare: [
      "48% of employees feel unsupported in AI adoption training.",
      "Prioritizing technical mastery at the expense of human skills (collaboration, adaptability).",
      "Retention risk: Employees leaving because they lack clear upskilling pathways."
    ],
    success: [
      "Integration of 'human skills' (critical thinking, leadership) alongside tech training.",
      "Personalized learning plans driving internal mobility and retention.",
      "A workforce adaptable to the 12 million predicted occupational transitions by 2030."
    ],
    coursera_unlock: "Professional Certificates + Leadership Academy"
  },
  {
    id: "c-suite",
    role: "C-Suite / Governance",
    title: "CEO / Risk & Compliance",
    desc: "Navigating regulatory complexity and responsible AI deployment.",
    nightmare: [
      "Rising regulatory complexity: 85% feel compliance is harder than 3 years ago.",
      "13% of organizations reporting AI model breaches due to lack of access controls.",
      "Reputational risk from biased or unsafe AI deployment."
    ],
    success: [
      "Strong 'tone at the top' sponsorship for compliance culture (ranked #1 factor).",
      "Equipping teams to interpret and apply complex regulations (ESG, cross-border).",
      "Deploying AI responsibly with robust ethics and governance frameworks."
    ],
    coursera_unlock: "Responsible AI Modules + Governance Briefings"
  },
  {
    id: "product",
    role: "Product & Data Leader",
    title: "CPO / Chief Data Officer",
    desc: "Orchestrating AI to deliver secure, scalable products.",
    nightmare: [
      "Non-technical teams underestimating resources needed for GenAI (63% agree).",
      "Lacking the data infrastructure skills to truly benefit from AI adoption.",
      "Slow time-to-market due to fragmented capabilities."
    ],
    success: [
      "Building critical capabilities in AI/ML data engineering and analytics.",
      "Accelerating delivery and improving product quality through expert content.",
      "Measurable improvement in innovation output and security."
    ],
    coursera_unlock: "Data Science Academy + Product Management Tracks"
  }
];

/* =========================
   3. Main Component
   ========================= */
export default function TechPersonaCarousel() {
  const [activeIdx, setActiveIdx] = useState(0);
  const activeData = TECH_DATA[activeIdx];

  return (
    <div className="carousel-wrap">
      <style>{CSS}</style>
      
      <div className="carousel-container">
        
        {/* Navigation Strip */}
        <div className="nav-strip">
          {TECH_DATA.map((item, idx) => (
            <button 
              key={item.id}
              className={`nav-btn ${idx === activeIdx ? 'active' : ''}`}
              onClick={() => setActiveIdx(idx)}
            >
              <div className="nav-circle" />
              {item.role}
            </button>
          ))}
        </div>

        {/* The "Lens" Card */}
        <div className="lens-card">
          {/* Header */}
          <div className="lens-header">
            <div>
              <span className="lens-tag">Tech Software • Executive Briefing</span>
              <h2 className="lens-title">{activeData.title}</h2>
              <p className="lens-sub">{activeData.desc}</p>
            </div>
          </div>

          {/* Grid Content */}
          <div className="lens-grid">
            
            {/* Left: Nightmares (Orange) */}
            <div className="lens-left">
              <div className="lens-h3 orange-text">
                <span style={{fontSize:16}}>⚠️</span> What Keeps Them Up At Night
              </div>
              <ul className="lens-list">
                {activeData.nightmare.map((point, i) => (
                  <li key={i}>{point}</li>
                ))}
              </ul>

              <div style={{marginTop: 40}}>
                <div className="lens-h3" style={{color:'rgba(255,255,255,0.5)'}}>
                  <span style={{fontSize:16}}>🔐</span> The Coursera Unlock
                </div>
                <div style={{fontStyle:'italic', color:'white', fontSize:18, borderLeft:'3px solid var(--brand)', paddingLeft:16}}>
                  {activeData.coursera_unlock}
                </div>
              </div>
            </div>

            {/* Right: Success (Green Card) */}
            <div className="lens-right">
              <div className="success-card">
                <div className="lens-h3 green-text" style={{marginBottom:16}}>
                  <span style={{fontSize:16}}>✅</span> What Success Looks Like
                </div>
                <ul className="success-list">
                  {activeData.success.map((point, i) => (
                    <li key={i}>
                      <div className="check-icon">✓</div>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>

        {/* Footer Citation */}
        <div className="source-footer">
          Insights derived from <b>"Tech Sector Executive Briefing: Bridging The 2025 Skills Gap"</b>
        </div>

      </div>
    </div>
  );
}
